import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Gift as GiftIcon, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { api, resolveImage } from "@/lib/api";

const GUIDES = [
  {
    slug: "for-him",
    title: "Funny Car Gifts For Him",
    tagline: "For the guy who already has everything except a personality in his cupholder.",
  },
  {
    slug: "funny-car-accessories",
    title: "Funny Car Accessories That Don't Look Cheap",
    tagline: "The gag-gift energy, minus the dollar-store aesthetic.",
  },
  {
    slug: "under-20",
    title: "Adult Novelty Gifts Under $20",
    tagline: "Stocking stuffers, white elephants, secret-Santa saves.",
  },
];

export default function GiftsHub() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Booty Breeze — Gift Guides",
    "description": "Curated funny, adult-appropriate car freshener gift ideas under $20. Hand-poured, small-batch, ships free over $10.",
    "url": "https://bootybreeze.com/gifts",
  };
  return (
    <div className="min-h-screen" data-testid="gifts-hub-page">
      <SEO
        title="Funny Car Freshener Gifts — Booty Breeze Gift Guides"
        description="Curated funny + adult-appropriate car freshener gifts. Hand-poured, under $10, ships discreetly. Stocking stuffers, gag gifts, and picks for the guy who has everything."
        path="/gifts"
        jsonLd={jsonLd}
      />

      <section className="max-w-4xl mx-auto px-6 sm:px-10 pt-20 pb-8 text-center">
        <GiftIcon className="mx-auto text-[#A855F7]" size={22} />
        <h1 className="font-display text-5xl sm:text-6xl leading-tight text-white mt-4">
          Actually funny. <br />
          <span className="text-neon-pink">Actually smells good.</span>
        </h1>
        <p className="text-white/70 mt-6 max-w-2xl mx-auto leading-relaxed">
          Every Booty Breeze is under $10, ships in discreet packaging, and lasts
          3–4 weeks. Picked well, they're the best-received gag gift on any
          list. Here are our curated shortlists.
        </p>
      </section>

      {/* GUIDES */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="grid md:grid-cols-3 gap-6" data-testid="gift-guide-list">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to={`/gifts/${g.slug}`}
              data-testid={`gift-guide-${g.slug}`}
              className="group rounded-2xl border border-white/10 bg-white/[0.02] p-7 hover:border-[#FF3D8B]/40 transition-colors"
            >
              <div className="text-[#A855F7] uppercase text-[10px] tracking-widest">
                Gift Guide
              </div>
              <h2 className="font-display text-2xl text-white mt-2 leading-tight group-hover:text-[#FF3D8B] transition-colors">
                {g.title}
              </h2>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{g.tagline}</p>
              <div className="mt-6 text-[#FF3D8B] text-xs uppercase tracking-widest inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                Read the picks <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY THEY WORK AS GIFTS */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-14 border-t border-white/10">
        <h2 className="font-display text-3xl text-white">Why they land as gifts</h2>
        <ul className="mt-6 space-y-4 text-white/70 leading-relaxed">
          <li className="flex gap-3"><Sparkles className="text-[#FF3D8B] shrink-0 mt-1" size={16} /><span><span className="text-white font-semibold">Under $10 each.</span> Perfect stocking stuffer, secret-Santa cap, or add-on.</span></li>
          <li className="flex gap-3"><Truck className="text-[#FF3D8B] shrink-0 mt-1" size={16} /><span><span className="text-white font-semibold">Free shipping over $10.</span> Grab two scents and postage is on us.</span></li>
          <li className="flex gap-3"><GiftIcon className="text-[#FF3D8B] shrink-0 mt-1" size={16} /><span><span className="text-white font-semibold">Discreet packaging.</span> Nothing on the outside of the box gives it away.</span></li>
        </ul>
      </section>

      {/* PREVIEW: top 3 products */}
      {products.slice(0, 3).length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-10 py-14 border-t border-white/10">
          <h2 className="font-display text-3xl text-white">The current lineup</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {products.slice(0, 3).map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-[#FF3D8B]/40 transition-colors"
              >
                <img
                  src={resolveImage(p.image_url, 400)}
                  alt={p.name}
                  loading="lazy"
                  className="w-full aspect-square object-cover"
                />
                <div className="p-4">
                  <div className="text-white font-semibold">{p.name}</div>
                  <div className="text-white/50 text-xs mt-1">${p.price?.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-16 text-center border-t border-white/10">
        <Link
          to="/shop"
          data-testid="gifts-shop-cta"
          className="pulse-pink inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
        >
          Shop the full collection <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
